import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { fmtNumber } from "@/components/Amount";   // Batch 11 · B7: 2 decimals everywhere
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState } from "react";
import LoadingNote from "@/components/LoadingNote";   // Batch 9 · A16
import DateInput from "@/components/DateInput";
import { fmtDateTimeFirm, fmtDate } from "@/lib/dt";
import { Link, useSearchParams } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";   // Batch 19 · Stage D: the one build setting
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import { Button } from "@/components/ui/button";
import Ref from "@/components/Ref";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import SourceBadge from "@/components/SourceBadge";
import { asArray } from "@/lib/asArray";
import { SURL_CODE_RX } from "@/components/SurlWorksheetDialog";   // Batch 26 · Add-on 1
import { STATIC_WS_CODES } from "@/lib/worksheetLinks";   // Step 11d-3 · find 4
import useMissingFields, { FieldMessage } from "@/lib/useMissingFields";   // Batch 27 · Add-on 1
import SaveCancelBar from "@/components/SaveCancelBar";   // Batch 28 · Follow-up 1 · F2
import { confirmDiscard } from "@/lib/unsavedChanges";
import { LinkExistingButton, UnlinkButton } from "@/components/LinkExistingMisstatement";   // Batch 34 · Z-16
import { offerLink } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import { I18N_B34J } from "@/lib/i18n/1310_b34j";   // Batch 34 · S16
import { errText } from "@/lib/i18n/1250_b34g";
import { ClassificationChangedBanner, AgingFitTag } from "@/components/SurlAgingB34L";   // Batch 34 · S19
import { AgingSuggestion, agingNeedsClass } from "@/components/AgingSuggestionP53S";     // PR 53 fixes · F4
import TbOnlyNotice, { isTbOnly } from "@/components/TbOnlyNotice";   // PR 3 · rule 1 (B-1a): TB-only → not applicable

const fmt = (n) => fmtNumber(Number(n || 0));      // Batch 11 · B7 (F-85): always 2 decimals (was max 2 → "-1,574.3")

// Batch 29 · M68: each uploaded post-year-end row gets an answer and a remark (saved with the worksheet's Save / Cancel bar);
// the conclusion warns while rows dated inside the window after the period end are "Not checked".
export const PYE_ANSWERS = ["next_year", "this_year", "this_year_trivial", "not_checked"];   // Batch 34 · S16: + below clearly trivial
export const PYE_TEXT = {
  en: { answer: "Answer", remark: "Remark", next_year: "Belongs to next year", this_year: "Relates to this year – send",
        not_checked: "Not checked", warn: "{n} post-year-end row(s) dated within {d} day(s) after the period end are not checked yet.",
        notSent: "{n} row(s) answered 'Relates to this year' are not sent to Misstatements yet.",
        this_year_trivial: I18N_B34J.en.b34j.pyeTrivial },
  ar: { answer: "الإجابة", remark: "ملاحظة", next_year: "يخص السنة التالية", this_year: "يخص هذه السنة – إرسال",
        not_checked: "لم يُفحص", warn: "عدد صفوف ما بعد نهاية السنة المؤرخة خلال {d} يوم بعد نهاية الفترة ولم تُفحص بعد: {n}.",
        notSent: "عدد الصفوف التي أجيب عنها بـ «يخص هذه السنة» ولم تُرسل إلى التحريفات بعد: {n}.",
        this_year_trivial: I18N_B34J.ar.b34j.pyeTrivial },
};

/** The post-year-end rows inside the window (period end < date ≤ period end + days after) still "Not checked" (or unanswered),
 *  and the rows answered "Relates to this year" not sent yet — the same rule as head_folder_worksheets.pye_check. */
export function pyeCheck(upload, daysAfter) {
  const pe = (upload && upload.period_end) || "";
  const rows = asArray(upload && upload.rows);
  let to = "";
  if (pe) {
    const d = new Date(`${String(pe).slice(0, 10)}T00:00:00Z`);
    if (!Number.isNaN(d.getTime())) { d.setUTCDate(d.getUTCDate() + Number(daysAfter ?? 5)); to = d.toISOString().slice(0, 10); }
  }
  const inWindow = rows.map((r, i) => ({ r, i })).filter(({ r }) => to && String(r.date || "").slice(0, 10) > String(pe).slice(0, 10)
                                                                   && String(r.date || "").slice(0, 10) <= to);
  const notChecked = inWindow.filter(({ r }) => !r.answer || r.answer === "not_checked").map(({ i }) => i);
  const notSent = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.answer === "this_year" && !r.misstatement_id).map(({ i }) => i);
  return { window_to: to, in_window: inWindow.length, not_checked: notChecked, not_sent: notSent };
}

/**
 * Batch A5 + A6 · minimal head-folder worksheet Dialog.
 * Opened by HeadFolders.jsx when ?open={code} is set, where code is
 * one of the five spec-sheet worksheets: 1203-06, 3203-06, 4101-06,
 * 5101-06, 1201-07.
 */
export default function HeadFolderWorksheetDialog() {
  const { currentEngId } = useEngagement();
  const { t, dict, lang } = useI18n();
  const agingCols = useResizableColumns({ storageKey: "hf-aging-accounts", defaults: [56, 100, 260, 130, 160], mins: [48, 80, 140, 100, 100] });
  const pyeCols = useResizableColumns({ storageKey: "hf-cutoff-pre-ye", defaults: [110, 130, 200, 130, 150, 220, 170, 90], mins: [96, 90, 120, 100, 110, 120, 120, 70] });
  const postCols = useResizableColumns({ storageKey: "hf-cutoff-post-ye", defaults: [110, 130, 200, 130, 220, 140, 200, 70], mins: [96, 90, 120, 100, 120, 100, 120, 56] });
  const [params, setParams] = useSearchParams();
  const code = params.get("open");
  // Batch 12 · L1 (F-90): a misstatement's link "…&row=<GL entry id>" opens the worksheet ON that row (marked, scrolled to)
  const focusRow = params.get("row") || "";
  // Batch 26 · Add-on 1: a liability head's -07 is the unrecorded-liabilities worksheet (SurlWorksheetDialog), not a cut-off test
  // Step 11d-3 · find 4: only the five fixed worksheets — "1201-06" is the Forms / Worksheets slot (HeadFolders opens its list)
  const isWorksheet = !!code && STATIC_WS_CODES.includes(code) && !SURL_CODE_RX.test(code);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState({});

  useEffect(() => {
    setDirty({});   // another worksheet / engagement: the draft belongs to the one that was open
    if (!isWorksheet || !currentEngId) { setData(null); return; }
    setLoading(true);
    api.get(`/engagements/${currentEngId}/head-folder-worksheets/${code}`, { _silent: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [isWorksheet, code, currentEngId]);
  const mf = useMissingFields(lang);     // Batch 27 · Add-on 1

  if (!isWorksheet) return null;

  // Batch 28 · Follow-up 1 · F2: the worksheet is one document (its rows included) — every edit stays in ``dirty`` until the
  // Save / Cancel bar's Save sends it in one PATCH (it used to be sent on every blur); Cancel drops the draft. Closing with
  // unsaved changes asks first; Mark complete, Send to Misstatements and the post-year-end upload wait until saved (they
  // write the worksheet themselves).
  const isDirty = Object.keys(dirty).length > 0;
  const SAVE_FIRST = lang === "ar" ? "احفظ التغييرات أولاً" : "Save the changes first";
  const close = async () => {
    if (!(await confirmDiscard(isDirty, lang))) return;
    const next = new URLSearchParams(params); next.delete("open");
    setParams(next, { replace: true });
  };

  const ws = data?.worksheet || {};
  const cfg = data?.config || {};
  const summary = data?.summary || {};
  const draft = { ...ws, ...dirty };
  const canComplete = cfg.kind === "aging"
    ? (!!draft.conclusion
       && (Number(summary.difference || 0) === 0 || !!draft.explanation)
       && (Number(summary.unclassified_count || 0) === 0))
    : !!draft.conclusion;

  // STEP 11d · A9: resolves true when saved (or nothing to save), false when the save was refused (the error is shown)
  const patch = async (extra = {}) => {
    setSaving(true);
    try {
      const body = { ...dirty, ...extra };
      if (!Object.keys(body).length) return true;
      const r = await api.patch(`/engagements/${currentEngId}/head-folder-worksheets/${code}`, body);
      setData((d) => ({ ...d, worksheet: r.data }));
      setDirty({});
      if (body.completed) mf.reset();
      // PR 53 fixes · F4: a Trade tick / reason changes the summary (difference, needs classification) — read it again
      if (body.trade_overrides || body.trade_override_reasons) await reloadWs();
      return true;
    } catch (e) {
      if (!mf.apply(e)) toast.error(errText(e, lang, t("common.failed")));   // Batch 34 · S16: the coded refusal in the screen's language
      return false;
    } finally { setSaving(false); }
  };

  // Batch 10 · B5 (F-77): the amount as the table shows it — the row's own amount, else debit − credit of the GL entry
  const rowAmount = (r) => {
    const a = r?.amount ?? (Number(r?.debit || 0) - Number(r?.credit || 0));
    const n = Number(a);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  };
  // Batch 29 · M65: after a link the server wrote the existing misstatement id on the row — read the worksheet again
  const reloadWs = async () => {
    const g = await api.get(`/engagements/${currentEngId}/head-folder-worksheets/${code}`, { _silent: true });
    setData(g.data);
  };
  const sendRowToMisstatements = async (row) => {
    if (sending[row.entry_id]) return;
    setSending((s) => ({ ...s, [row.entry_id]: true }));
    // STEP 11d · A9 (find 30): the worksheet is one document — its unsaved changes are saved first; a refused save shows its
    // error and nothing is sent
    if (isDirty && !(await patch())) { setSending((s) => ({ ...s, [row.entry_id]: false })); return; }
    try {
      const r = await api.post(`/engagements/${currentEngId}/misstatements/send-from-finding`, {
        voucher: row.voucher_no || "", party: row.vendor_customer || row.party || "",   // Batch 29 · M65: the item key
        source_kind: "cutoff",
        source_id: `${code}:${row.entry_id}`,
        fs_head: cfg.fs_head,
        description: `Cut-off exception (${code}): ${row.description || row.voucher_no || row.entry_id}`.slice(0, 500),
        // Batch 10 · B5 (F-77): the amount the row SHOWS (the GL entry has debit / credit, no "amount" field — it arrived as 0).
        // Batch 31 · item 11 (M94): the effects are set by the server from this signed amount (the sign rule: the effect of the
        // correcting entry — SI-2025-02706 stays −45,430.05 on profit and on net assets); they stay editable on Misstatements
        amount: rowAmount(row),
        mtype: "Factual",
        link_back: `/head-folders?open=${code}&row=${row.entry_id}`,
      });
      const newRows = asArray(draft.rows).map((rr) => rr.entry_id === row.entry_id ? { ...rr, misstatement_id: r.data.id } : rr);
      setDirty((d) => ({ ...d, rows: newRows }));
      await patch({ rows: newRows });
      toast.success(t("disc.a3.sentToast"));
    } catch (e) {
      try {
        const done = await offerLink(e, { engId: currentEngId, lang, target: { type: "cutoff_row", code, entry_id: row.entry_id } });
        if (done === "linked") await reloadWs();
        else if (!done) toast.error(errText(e, lang, t("common.failed")));       // Step 12 add-on G6.1: one helper (Cancel = "kept")
      } catch (e2) { toast.error(errText(e2, lang, t("common.failed"))); }
    } finally { setSending((s) => ({ ...s, [row.entry_id]: false })); }
  };

  // Batch 9 · A3: the title comes from the worksheet CODE, so the first render (before the data arrives) already says
  // "AR aging" for 1203-06 instead of "Cut-off test"
  const kindFromCode = (code === "1203-06" || code === "3203-06") ? "aging" : "cutoff";
  const headFromCode = String(code || "").slice(0, 4);
  const title = (cfg.kind || kindFromCode) === "aging"
    ? ((cfg.fs_head || headFromCode) === "1203" ? t("hfWs.aging.arTitleShort") : t("hfWs.aging.apTitleShort"))
    : t("hfWs.cutoff.titleShort");
  // Batch 9 · A14 (F-68): a post-year-end row (unrecorded liability / early recording) can be sent to Misstatements
  const sendPyeRowToMisstatements = async (r, i) => {
    const key = `pye-${i}`;
    if (sending[key]) return;
    setSending((x) => ({ ...x, [key]: true }));
    if (isDirty && !(await patch())) { setSending((x) => ({ ...x, [key]: false })); return; }   // STEP 11d · A9 (find 30)
    try {
      const res = await api.post(`/engagements/${currentEngId}/misstatements/send-from-finding`, {
        voucher: r.voucher || "",          // Batch 29 · M65: the item key (party + invoice no. are read from the description)
        source_kind: "cutoff_post_year_end",
        source_id: `${code}:pye:${i}:${r.voucher || r.date || ""}`,
        fs_head: cfg.fs_head,
        description: `Cut-off — post-year-end item (${code}): ${r.description || r.voucher || r.date || ""}`.slice(0, 500),
        amount: rowAmount(r),          // Batch 10 · B5 · Batch 31 · item 11 (M94): effects set by the server (entry to record: −amount)
        mtype: "Factual",
        link_back: `/head-folders?open=${code}`,
      });
      const rows = asArray(draft.post_year_end_upload?.rows).map((rr, j) => j === i ? { ...rr, misstatement_id: res.data.id } : rr);
      const pye = { ...(draft.post_year_end_upload || {}), rows };
      setDirty((d) => ({ ...d, post_year_end_upload: pye }));
      await patch({ post_year_end_upload: pye });
      toast.success(t("disc.a3.sentToast"));
    } catch (e) {
      try {
        const done = await offerLink(e, { engId: currentEngId, lang, target: { type: "cutoff_pye", code, index: i } });
        if (done === "linked") await reloadWs();
        else if (!done) toast.error(errText(e, lang, t("common.failed")));       // Step 12 add-on G6.1: one helper (Cancel = "kept")
      } catch (e2) { toast.error(errText(e2, lang, t("common.failed"))); }
    } finally { setSending((x) => ({ ...x, [key]: false })); }
  };
  const PYE_SEND = lang === "ar" ? "إرسال إلى التحريفات" : "Send to misstatements";
  const PT = PYE_TEXT[lang === "ar" ? "ar" : "en"];
  // Batch 29 · M68: one answer + remark per uploaded post-year-end row, kept in the draft (saved with the bar)
  const setPyeRow = (i, patchRow) => {
    const rows = asArray(draft.post_year_end_upload?.rows).map((rr, j) => (j === i ? { ...rr, ...patchRow } : rr));
    setDirty((d) => ({ ...d, post_year_end_upload: { ...(draft.post_year_end_upload || {}), rows } }));
  };
  const pyeGate = pyeCheck(draft.post_year_end_upload, draft.window_days_after ?? 5);

  // Batch A Round-2 F3 — cut-off Mark complete needs conclusion + preparer + date.
  const cutoffCanComplete = !!(draft.conclusion && draft.preparer_name && draft.conclusion_date);

  return (
    <FullScreenDialog open={true} onClose={close} testid="hfws-dialog"
                      title={<span data-testid={`hfws-title-${code}`}>
            <Ref>{code}</Ref> {title} <SourceBadge source={data?.source} className="ms-2 align-middle" />
      </span>}>
        <div className="pe-1" data-testid="hfws-scroll">
        {data && <SignOffBar itemType="hf_worksheet" itemKey={code} refreshKey={data.updated_at} missing={mf.missing} saveFirst={isDirty}
                                onChange={() => api.get(`/engagements/${currentEngId}/head-folder-worksheets/${code}`, { _silent: true }).then((r) => setData(r.data)).catch(() => {})} />}
        {loading && <div className="p-6"><LoadingNote active what={title} /></div>}
        {!loading && data && cfg.kind === "aging" && (
          <div className="space-y-3">
            <ClassificationChangedBanner missing={data.completion_missing} />
            {!summary.aging_present ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
                   data-testid="hfws-no-aging">
                <div className="mb-2 font-semibold">{t("hfWs.aging.noAgingUpload")}</div>
                <a href={`/aging/legacy?upload=${cfg.aging_kind || (cfg.fs_head === "3203" ? "ap" : "ar")}`}
                   className="inline-flex items-center gap-1 rounded-md border border-amber-400 bg-white px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 dark:bg-slate-900"
                   data-testid="hfws-upload-aging-btn">
                  {t("aging.uploadAr")} / {t("aging.uploadAp")}
                </a>
                <div className="mt-2 text-[10px]">
                  {t("hfWs.aging.tbBalance")}: <b>{fmt(summary.tb_balance)}</b>
                  {" · "}
                  {t("hfWs.aging.folderTotal")}: <b>{fmt(summary.folder_total)}</b>
                </div>
              </div>
            ) : (
            <table className="w-full text-sm">
              <tbody className="divide-y">
                <tr><td className="py-1 text-muted-foreground">{t("hfWs.aging.agingTotal")}</td>
                  <td className="py-1 text-end font-mono" data-testid="hfws-aging-total">{fmt(summary.aging_total)}</td></tr>
                {summary.aging_filename && (
                  <tr><td className="py-1 text-muted-foreground">{t("hfWs.aging.linkedUpload")}</td>
                    <td className="py-1 text-end text-xs" data-testid="hfws-aging-upload">
                      {summary.aging_filename}{summary.aging_as_of ? ` · ${fmtDate(summary.aging_as_of)}` : ""}
                    </td></tr>
                )}
                <tr><td className="py-1 text-muted-foreground">
                  {summary.coa_filter?.fallback === "folder_total"
                    ? `${t("hfWs.aging.tbBalance")} — folder ${cfg.fs_head} (all accounts)`
                    : t("hfWs.aging.tbBalance")}
                </td>
                  <td className="py-1 text-end font-mono" data-testid="hfws-tb">{fmt(summary.tb_balance)}</td></tr>
                <tr><td className="py-1 text-muted-foreground">{t("hfWs.aging.folderTotal")}</td>
                  <td className="py-1 text-end font-mono" data-testid="hfws-folder-total">{fmt(summary.folder_total)}</td></tr>
                <tr className={Number(summary.difference || 0) !== 0 ? "bg-amber-50 dark:bg-amber-950/20" : ""}>
                  <td className="py-1 font-semibold">{t("hfWs.aging.difference")}</td>
                  <td className="py-1 text-end font-mono font-semibold" data-testid="hfws-diff">{fmt(summary.difference)}</td></tr>
              </tbody>
            </table>
            )}
            <div className="text-[10px] text-muted-foreground" data-testid="hfws-coa-note">
              {t("hfWs.aging.tbTrade")}
            </div>

            {/* A5 Round-2 UI · per-account trade list */}
            <div className="rounded border" data-testid="hfws-accounts-table">
              <div className="border-b bg-slate-50 px-2 py-1 text-xs font-semibold dark:bg-slate-900/40">
                {t("hfWs.aging.accountsTitle")}
                <span className="ms-2 text-[10px] text-muted-foreground">
                  ({(summary.accounts || []).length})
                </span>
                {summary.unclassified_count > 0 && (
                  <span className="ms-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                        data-testid="hfws-unclassified-count">
                    {t("hfWs.aging.needsClassification")}: {summary.unclassified_count}
                  </span>
                )}
              </div>
              <div className="max-h-48 overflow-auto">
                <ResizableTable cols={agingCols} className="w-full text-[11px]">
                  <thead className="border-b bg-slate-50/40 dark:bg-slate-900/20">
                    <tr>
                      <ResizableTh index={0} className="px-2 py-1 text-center w-8">{t("hfWs.aging.colTrade")}</ResizableTh>
                      <ResizableTh index={1} className="px-2 py-1 text-start">{t("hfWs.aging.colCode")}</ResizableTh>
                      <ResizableTh index={2} className="px-2 py-1 text-start">{t("hfWs.aging.colName")}</ResizableTh>
                      <ResizableTh index={3} className="px-2 py-1 text-end">{t("hfWs.aging.colBalance")}</ResizableTh>
                      <ResizableTh index={4} className="px-2 py-1 text-start">{t("hfWs.aging.colTag")}</ResizableTh>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {asArray(summary.accounts).map((a) => {
                      const overrides = { ...(ws.trade_overrides || {}), ...(dirty.trade_overrides || {}) };
                      const isTrade = (a.coa_id in overrides) ? overrides[a.coa_id] : (a.suggested_trade === true);
                      const reasons = { ...(ws.trade_override_reasons || {}), ...(dirty.trade_override_reasons || {}) };
                      const setOverride = (v) => {
                        const next = { ...overrides, [a.coa_id]: v };
                        setDirty((d) => ({ ...d, trade_overrides: next }));   // saved with the bar's Save (F2)
                      };
                      const toggle = () => setOverride(!isTrade);
                      const setReason = (v) => setDirty((d) => ({ ...d, trade_override_reasons: { ...reasons, [a.coa_id]: v } }));
                      return (
                        <tr key={a.coa_id || `_${a.code}`} data-testid={`hfws-acct-row-${a.coa_id}`}>
                          <td className="px-2 py-1 text-center">
                            <input type="checkbox" checked={!!isTrade} onChange={toggle}
                                   disabled={draft.completed}
                                   data-testid={`hfws-acct-trade-${a.coa_id}`} />
                          </td>
                          <td className="px-2 py-1 font-mono">{a.code || a.name || "—"}</td>
                          <td className="px-2 py-1">{a.name ? a.name : <span className="italic text-muted-foreground">({t("hfWs.aging.blankName")})</span>}</td>
                          <td className="px-2 py-1 text-end font-mono">{fmt(a.balance)}</td>
                          <td className="px-2 py-1">
                            {a.aging_suggestion ? (
                              <AgingSuggestion a={a} overrides={overrides} reasons={reasons} disabled={draft.completed}
                                               onReason={setReason} onKeepTrade={() => setOverride(true)} />
                            ) : agingNeedsClass(a, overrides, reasons) ? (
                              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                                    data-testid={`hfws-acct-needs-${a.coa_id}`}>
                                {t("hfWs.aging.needsClassification")}
                              </span>
                            ) : a.coa_id in overrides ? (
                              <span className="text-[10px] text-emerald-700">{t("hfWs.aging.overridden")}</span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground">
                                {a.suggested_trade === true ? t("hfWs.aging.suggestedTrade")
                                  : t("hfWs.aging.suggestedNonTrade")}
                              </span>
                            )}
                            <AgingFitTag a={a} />{/* PR 53 · F4: only when the aging lists accounts */}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </ResizableTable>
              </div>
            </div>
            {summary.aging_present && Number(summary.difference || 0) !== 0 && (
              <div>
                <label className="block text-xs font-medium">{t("hfWs.aging.explanation")}</label>
                <textarea rows={2}
                          value={draft.explanation || ""}
                          onChange={(e) => { setDirty((d) => ({ ...d, explanation: e.target.value })); mf.filled("explanation", e.target.value); }}
                          {...mf.props("explanation", "mt-1 w-full rounded border px-2 py-1 text-xs dark:bg-slate-900")}
                          data-testid="hfws-explanation" />
                <FieldMessage mf={mf} field="explanation" testid="hfws-explanation-missing" />
                <div className="text-[10px] text-amber-700">{t("hfWs.aging.explanationReq")}</div>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium">{t("hfWs.aging.conclusion")}</label>
              <textarea rows={2}
                        value={draft.conclusion || ""}
                        onChange={(e) => { setDirty((d) => ({ ...d, conclusion: e.target.value })); mf.filled("conclusion", e.target.value); }}
                        {...mf.props("conclusion", "mt-1 w-full rounded border px-2 py-1 text-xs dark:bg-slate-900")}
                        data-testid="hfws-conclusion" />
              <FieldMessage mf={mf} field="conclusion" testid="hfws-conclusion-missing" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-end justify-end gap-2">
                {draft.completed && (
                  <span className="rounded-md bg-emerald-100 px-2 py-1 text-[10px] text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
                    <CheckCircle2 className="me-1 inline h-3 w-3" />
                    {t("hfWs.aging.completedBadge")}{draft.completed_at ? ` · ${fmtDateTimeFirm(draft.completed_at)}` : ""}
                  </span>
                )}
                <Button size="sm"
                        disabled={saving || draft.completed || isDirty}
                        onClick={() => patch({ completed: true })}
                        title={!summary.aging_present ? t("hfWs.aging.noAgingUpload") : (!canComplete ? t("hfWs.aging.markCompleteGate") : "")}
                        data-testid="hfws-mark-complete">
                  {t("hfWs.aging.markComplete")}
                </Button>
                <FieldMessage mf={mf} field="aging_upload" testid="hfws-aging-upload-missing" />
                <FieldMessage mf={mf} field="classification" testid="hfws-classification-missing" />
              </div>
            </div>
          </div>
        )}
        {!loading && data && cfg.kind === "cutoff" && (
          <div className="space-y-3">
            <div className="text-[10px] text-muted-foreground" data-testid="hfws-std">
              {((dict?.hfWs?.cutoff?.stdCodes) || ["ISA 330", "ISA 500"]).map((code, i, arr) => (
                <React.Fragment key={code}>
                  <Ref>{code}</Ref>
                  {i < arr.length - 1 && (dict?.hfWs?.cutoff?.stdSep || "; ")}
                </React.Fragment>
              ))}
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <label className="block">
                <span className="text-[10px] uppercase text-muted-foreground">{t("hfWs.cutoff.windowDays")}</span>
                <span className="ms-1 inline-flex gap-1">
                  <input type="number" min={0} className="w-16 rounded border px-1 py-0.5 text-xs dark:bg-slate-900"
                         value={draft.window_days_before ?? 5}
                         onChange={(e) => setDirty((d) => ({ ...d, window_days_before: Number(e.target.value) }))}
                         data-testid="hfws-days-before" />
                  <span>/</span>
                  <input type="number" min={0} className="w-16 rounded border px-1 py-0.5 text-xs dark:bg-slate-900"
                         value={draft.window_days_after ?? 5}
                         onChange={(e) => setDirty((d) => ({ ...d, window_days_after: Number(e.target.value) }))}
                         data-testid="hfws-days-after" />
                </span>
              </label>
            </div>
            <div className="text-xs font-semibold">{t("hfWs.cutoff.preYearEnd")}</div>
            <div className="max-h-64 overflow-auto rounded border" data-testid="hfws-pye-table">
              <ResizableTable cols={pyeCols} className="w-full text-[11px]">
                <thead className="border-b bg-slate-50 dark:bg-slate-900/40">
                  <tr>
                    <ResizableTh index={0} className="px-2 py-1 text-start">{t("hfWs.cutoff.colDate")}</ResizableTh>
                    <ResizableTh index={1} className="px-2 py-1 text-start">{t("hfWs.cutoff.colVoucher")}</ResizableTh>
                    <ResizableTh index={2} className="px-2 py-1 text-start">{t("hfWs.cutoff.colAccount")}</ResizableTh>
                    <ResizableTh index={3} className="px-2 py-1 text-end">{t("hfWs.cutoff.colAmount")}</ResizableTh>
                    <ResizableTh index={4} className="px-2 py-1 text-start">{t("hfWs.cutoff.recordedCorrectly")}</ResizableTh>
                    <ResizableTh index={5} className="px-2 py-1 text-start">{t("hfWs.cutoff.remarks")}</ResizableTh>
                    <ResizableTh index={6} className="px-2 py-1 text-start">{t("hfWs.cutoff.supportDocLabel")}</ResizableTh>
                    <ResizableTh index={7} className="px-2 py-1"></ResizableTh>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(data.pre_year_end_entries || []).length === 0 && (
                    <tr><td colSpan={8} className="px-2 py-4 text-center text-muted-foreground">
                      {isTbOnly(data.pre_year_end_entries_status)
                        ? <TbOnlyNotice status={data.pre_year_end_entries_status} testid="cutoff-tb-only" />
                        : (cfg.fs_head && (data.pre_year_end_entries === undefined))
                        ? t("hfWs.cutoff.noAccounts") : "—"}
                    </td></tr>
                  )}
                  {asArray(data.pre_year_end_entries).map((e) => {
                    const row = (asArray(draft.rows).find((r) => r.entry_id === e.id)) || { entry_id: e.id, recorded_correctly: "", remarks: "" };
                    const setRow = (patchRow) => {
                      const rows = (asArray(draft.rows).some((r) => r.entry_id === e.id))
                        ? asArray(draft.rows).map((r) => r.entry_id === e.id ? { ...r, ...patchRow } : r)
                        : [...(draft.rows || []), { ...row, ...patchRow }];
                      setDirty((d) => ({ ...d, rows }));
                    };
                    return (
                      <tr key={e.id} data-testid={`hfws-pye-row-${e.id}`}
                          data-focused={focusRow === e.id ? "true" : undefined}
                          ref={focusRow === e.id ? (el) => { if (el && !el.dataset.scrolled) { el.dataset.scrolled = "1"; el.scrollIntoView({ block: "center" }); } } : undefined}
                          className={focusRow === e.id ? "bg-amber-50 outline outline-2 outline-amber-400 dark:bg-amber-950/30" : undefined}>
                        <td className="px-2 py-1 font-mono">{fmtDate(e.entry_date) || e.entry_date}</td>
                        <td className="px-2 py-1 font-mono">{e.voucher_no || "—"}</td>
                        <td className="px-2 py-1 font-mono">{e.account_code || "—"}</td>
                        <td className="px-2 py-1 text-end font-mono">{fmt(e.amount ?? (Number(e.debit || 0) - Number(e.credit || 0)))}</td>
                        <td className="px-2 py-1">
                          <select value={row.recorded_correctly || ""}
                                  onChange={(ev) => setRow({ recorded_correctly: ev.target.value })}
                                  className="rounded border px-1 py-0.5 text-[11px] dark:bg-slate-900"
                                  data-testid={`hfws-yes-no-${e.id}`}>
                            <option value="">—</option>
                            <option value="yes">{t("hfWs.cutoff.yes")}</option>
                            <option value="no">{t("hfWs.cutoff.no")}</option>
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <input value={row.remarks || ""}
                                 onChange={(ev) => setRow({ remarks: ev.target.value })}
                                 className="w-full rounded border px-1 py-0.5 text-[11px] dark:bg-slate-900"
                                 data-testid={`hfws-remarks-${e.id}`} />
                        </td>
                        <td className="px-2 py-1">
                          <RowSupportUpload engagementId={currentEngId}
                                            contextId={`${code}:${e.id}`}
                                            disabled={!!draft.completed} />
                        </td>
                        <td className="px-2 py-1 text-end">
                          {!row.misstatement_id && row.recorded_correctly === "no" && (
                            <button
                              onClick={() => sendRowToMisstatements({ ...e, ...row })}
                              disabled={!!sending[e.id]}
                              className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
                              data-testid={`hfws-send-${e.id}`}>
                              <Send className="h-3 w-3" /> {t("hfWs.cutoff.sendBtn")}
                            </button>
                          )}
                          {!row.misstatement_id && row.recorded_correctly === "no" && (   /* Batch 34 · Z-16 */
                            <span className="ms-1"><LinkExistingButton engId={currentEngId} disabled={!!draft.completed || isDirty} onDone={reloadWs}
                              testid={`hfws-linkms-${e.id}`} body={{ source_kind: "cutoff", source_id: `${code}:${e.id}`,
                                                                     target: { type: "cutoff_row", code, entry_id: e.id } }} /></span>
                          )}
                          {row.misstatement_id && row.misstatement_linked && (
                            <UnlinkButton engId={currentEngId} disabled={!!draft.completed || isDirty} onDone={reloadWs} testid={`hfws-unlink-${e.id}`}
                                          body={{ source_kind: "cutoff", source_id: `${code}:${e.id}`, target: { type: "cutoff_row", code, entry_id: e.id } }} />
                          )}
                          {row.misstatement_id && (
                            <span className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300"
                                  data-testid={`hfws-sent-${e.id}`}>
                              <CheckCircle2 className="h-3 w-3" /> {t("hfWs.cutoff.sentIndicator")}
                            </span>
                          )}
                          {row.misstatement_id && (   /* Batch 11 · B6 (F-84): send again = refresh amount + effects from this row */
                            <button onClick={() => sendRowToMisstatements({ ...e, ...row })} disabled={!!sending[e.id]}
                                    className="ms-1 text-[10px] text-sky-700 underline disabled:opacity-50" data-testid={`hfws-resend-${e.id}`}>
                              {t("hfWs.cutoff.resendBtn")}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </ResizableTable>
            </div>
            <div className="text-xs font-semibold">{t("hfWs.cutoff.postYearEnd")}</div>
            <div className="text-[10px] text-muted-foreground">
              {t("hfWs.cutoff.uploadPye")} · {t("hfWs.cutoff.uploadPyeCols")}
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="hfws-pye-upload-row">
              <input type="file" accept=".xlsx,.csv" disabled={isDirty} title={isDirty ? SAVE_FIRST : undefined}
                     onChange={async (ev) => {
                       const f = ev.target.files?.[0];
                       if (!f) return;
                       const form = new FormData(); form.append("file", f);
                       try {
                         const r = await api.post(`/engagements/${currentEngId}/head-folder-worksheets/${code}/post-year-end-upload`, form, { headers: { "Content-Type": "multipart/form-data" } });
                         setData((d) => ({ ...d, worksheet: r.data.worksheet }));
                         toast.success(`${r.data.kept} rows kept · after ${r.data.period_end}`);
                       } catch (e) {
                         toast.error(e?.response?.data?.detail || t("common.failed"));
                       } finally { ev.target.value = ""; }
                     }}
                     className="text-[11px]"
                     data-testid="hfws-pye-file-input" />
              {draft.post_year_end_upload?.filename && (
                <span className="text-[10px] text-muted-foreground">
                  · {draft.post_year_end_upload.filename} · {(draft.post_year_end_upload.rows || []).length} row(s)
                </span>
              )}
            </div>
            {/* Batch A Round-2 F2 — post-year-end documents upload */}
            <div className="text-xs font-semibold">{t("hfWs.cutoff.uploadDocs")}</div>
            <div className="text-[10px] text-muted-foreground">{t("hfWs.cutoff.uploadDocsHint")}</div>
            <PyeDocsUpload engagementId={currentEngId} contextId={`${code}:pye_docs`}
                            disabled={!!draft.completed} />
            {(draft.post_year_end_upload?.rows || []).length > 0 && (
              <div className="max-h-40 overflow-auto rounded border" data-testid="hfws-pye-list">
                <ResizableTable cols={postCols} className="w-full text-[11px]">
                  <thead className="border-b bg-slate-50 dark:bg-slate-900/40">
                    <tr>
                      <ResizableTh index={0} className="px-2 py-1 text-start">{t("hfWs.cutoff.colDate")}</ResizableTh>
                      <ResizableTh index={1} className="px-2 py-1 text-start">{t("hfWs.cutoff.colVoucher")}</ResizableTh>
                      <ResizableTh index={2} className="px-2 py-1 text-start">{t("hfWs.cutoff.colAccount")}</ResizableTh>
                      <ResizableTh index={3} className="px-2 py-1 text-end">{t("hfWs.cutoff.colAmount")}</ResizableTh>
                      <ResizableTh index={4} className="px-2 py-1 text-start">{t("b3.description")}</ResizableTh>
                      <ResizableTh index={5} className="px-2 py-1 text-start">{PT.answer}</ResizableTh>
                      <ResizableTh index={6} className="px-2 py-1 text-start">{PT.remark}</ResizableTh>
                      <ResizableTh index={7} className="px-2 py-1 text-start"></ResizableTh>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {asArray(draft.post_year_end_upload.rows).map((r, i) => (
                      <tr key={i} data-testid={`hfws-pye-upload-row-${i}`}
                          data-not-checked={pyeGate.not_checked.includes(i) ? "1" : "0"}
                          className={pyeGate.not_checked.includes(i) ? "bg-amber-50/60 dark:bg-amber-950/20" : undefined}>
                        <td className="px-2 py-1 font-mono">{fmtDate(r.date) || r.date}</td>
                        <td className="px-2 py-1 font-mono">{r.voucher || "—"}</td>
                        <td className="px-2 py-1 font-mono">{r.account || "—"}</td>
                        <td className="px-2 py-1 text-end font-mono">{fmt(r.amount)}</td>
                        <td className="px-2 py-1">{r.description || "—"}</td>
                        <td className="px-2 py-1">
                          <select value={r.answer || ""} disabled={!!draft.completed}
                                  onChange={(ev) => setPyeRow(i, { answer: ev.target.value })}
                                  className="rounded border px-1 py-0.5 text-[11px] dark:bg-slate-900"
                                  data-testid={`hfws-pye-answer-${i}`}>
                            <option value="">—</option>
                            {PYE_ANSWERS.map((a) => <option key={a} value={a}>{PT[a]}</option>)}
                          </select>
                        </td>
                        <td className="px-2 py-1">
                          <input value={r.remark || ""} disabled={!!draft.completed}
                                 onChange={(ev) => setPyeRow(i, { remark: ev.target.value })}
                                 className="w-full min-w-[8rem] rounded border px-1 py-0.5 text-[11px] dark:bg-slate-900"
                                 data-testid={`hfws-pye-remark-${i}`} />
                        </td>
                        <td className="px-2 py-1">
                          {r.misstatement_id
                            ? <Link className="text-emerald-700 underline" to="/completion/misstatements" data-testid={`hfws-pye-sent-${i}`}><CheckCircle2 className="inline h-3 w-3" /> {t("disc.a3.sentToast")}</Link>
                            : <button type="button" disabled={!!draft.completed || !!sending[`pye-${i}`]} onClick={() => sendPyeRowToMisstatements(r, i)}
                                      className="rounded border px-1.5 py-0.5 text-[10px] hover:bg-slate-50 dark:hover:bg-slate-800" data-testid={`hfws-pye-send-${i}`}>
                                <Send className="inline h-3 w-3" /> {PYE_SEND}
                              </button>}
                          {!r.misstatement_id && r.answer === "this_year" && (   /* Batch 34 · Z-16 */
                            <span className="ms-1"><LinkExistingButton engId={currentEngId} disabled={!!draft.completed || isDirty} onDone={reloadWs}
                              testid={`hfws-pye-linkms-${i}`} body={{ source_kind: "cutoff_post_year_end", source_id: `${code}:pye:${i}:${r.voucher || r.date || ""}`,
                                                                      target: { type: "cutoff_pye", code, index: i } }} /></span>)}
                          {r.misstatement_id && r.misstatement_linked && (
                            <UnlinkButton engId={currentEngId} disabled={!!draft.completed || isDirty} onDone={reloadWs} testid={`hfws-pye-unlink-${i}`}
                                          body={{ source_kind: "cutoff_post_year_end", source_id: `${code}:pye:${i}:${r.voucher || r.date || ""}`,
                                                  target: { type: "cutoff_pye", code, index: i } }} />)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </ResizableTable>
              </div>
            )}
            {(pyeGate.not_checked.length > 0 || pyeGate.not_sent.length > 0) && (   /* Batch 29 · M68: a warning, not a block */
              <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                   role="status" data-testid="hfws-pye-gate-warning">
                {pyeGate.not_checked.length > 0 && (
                  <div data-testid="hfws-pye-not-checked">{PT.warn.replace("{n}", String(pyeGate.not_checked.length)).replace("{d}", String(draft.window_days_after ?? 5))}</div>
                )}
                {pyeGate.not_sent.length > 0 && (
                  <div data-testid="hfws-pye-not-sent">{PT.notSent.replace("{n}", String(pyeGate.not_sent.length))}</div>
                )}
              </div>
            )}
            <div>
              <label className="block text-xs font-medium">{t("hfWs.cutoff.conclusion")}</label>
              <textarea rows={2} value={draft.conclusion || ""}
                        onChange={(e) => { setDirty((d) => ({ ...d, conclusion: e.target.value })); mf.filled("conclusion", e.target.value); }}
                        {...mf.props("conclusion", "mt-1 w-full rounded border px-2 py-1 text-xs dark:bg-slate-900")}
                        data-testid="hfws-conclusion" />
              <FieldMessage mf={mf} field="conclusion" testid="hfws-conclusion-missing" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium">{t("hfWs.cutoff.conclusionDate")}</label>
                <div {...mf.props("conclusion_date", "rounded")}>
                <DateInput value={draft.conclusion_date || ""}
                       onChange={(v) => { setDirty((d) => ({ ...d, conclusion_date: v })); mf.filled("conclusion_date", v); }}
                       className="mt-1 w-full rounded border px-2 py-1 text-xs dark:bg-slate-900"
                       data-testid="hfws-conclusion-date" />
                </div>
                <FieldMessage mf={mf} field="conclusion_date" testid="hfws-conclusion-date-missing" />
              </div>
              <div className="flex items-end justify-end gap-2">
                {draft.completed && (
                  <span className="rounded-md bg-emerald-100 px-2 py-1 text-[10px] text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
                    <CheckCircle2 className="me-1 inline h-3 w-3" />
                    {t("hfWs.aging.completedBadge")}
                  </span>
                )}
                <Button size="sm"
                        disabled={saving || draft.completed || isDirty}
                        onClick={() => patch({ completed: true })}
                        title={!cutoffCanComplete ? t("hfWs.cutoff.markCompleteGate") : ""}
                        data-testid="hfws-mark-complete">
                  {t("hfWs.cutoff.markComplete")}
                </Button>
              </div>
            </div>
          </div>
        )}
        {data && <SaveCancelBar dirty={isDirty} saving={saving} onSave={() => patch()} onCancel={() => setDirty({})} lang={lang} testid="hfws-save-bar" />}
        </div>
    </FullScreenDialog>
  );
}

/**
 * Batch A Round-2 F1 — per-row Support upload. Uses the generic
 * /attachments endpoint with context_type="cutoff" and
 * context_id="{code}:{entry_id}". Replacing a file uploads a new one;
 * nothing is deleted (R7). Every upload is audit-trailed by the
 * backend attachments handler.
 */
function RowSupportUpload({ engagementId, contextId, disabled }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const load = React.useCallback(async () => {
    if (!engagementId || !contextId) return;
    try {
      const r = await api.get(`/attachments`, {
        params: { engagement_id: engagementId, context_type: "cutoff", context_id: contextId },
        _silent: true,
      });
      setItems(r.data || []);
    } catch { /* silent */ }
  }, [engagementId, contextId]);
  useEffect(() => { load(); }, [load]);
  const onFile = async (ev) => {
    const f = ev.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("engagement_id", engagementId);
      fd.append("context_type", "cutoff");
      fd.append("context_id", contextId);
      await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
      toast.success(f.name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally {
      setBusy(false); if (inputRef.current) inputRef.current.value = "";
    }
  };
  const latest = items[items.length - 1];
  const backend = BACKEND_URL || "";   // Batch 19 · Stage D: the one build setting, read in lib/api.js
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`hfws-support-${contextId}`}>
      <input ref={inputRef} type="file" onChange={onFile} disabled={disabled || busy}
             className="hidden"
             accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
             data-testid={`hfws-support-input-${contextId}`} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={disabled || busy}
              className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
              data-testid={`hfws-support-btn-${contextId}`}>
        {latest ? t("hfWs.cutoff.replace") : t("hfWs.cutoff.supportDocLabel")}
      </button>
      {latest && (
        <a href={`${backend}/api/attachments/${latest.id}/download`} target="_blank" rel="noreferrer"
           className="text-[10px] text-blue-700 underline"
           data-testid={`hfws-support-view-${contextId}`}>
          {latest.filename}
        </a>
      )}
    </div>
  );
}

/**
 * Batch A Round-2 F2 — post-year-end documents. Multi-file upload
 * via /attachments with context_type="cutoff",
 * context_id="{code}:pye_docs". Each row lists name / uploader / date.
 */
function PyeDocsUpload({ engagementId, contextId, disabled }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const load = React.useCallback(async () => {
    if (!engagementId || !contextId) return;
    try {
      const r = await api.get(`/attachments`, {
        params: { engagement_id: engagementId, context_type: "cutoff", context_id: contextId },
        _silent: true,
      });
      setItems(r.data || []);
    } catch { /* silent */ }
  }, [engagementId, contextId]);
  useEffect(() => { load(); }, [load]);
  const onFiles = async (ev) => {
    const files = Array.from(ev.target.files || []); if (!files.length) return;
    setBusy(true);
    let ok = 0;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("engagement_id", engagementId);
        fd.append("context_type", "cutoff");
        fd.append("context_id", contextId);
        try {
          await api.post("/attachments", fd, { headers: { "Content-Type": "multipart/form-data" } });
          ok += 1;
        } catch (e) {
          toast.error(`${f.name}: ${e?.response?.data?.detail || t("common.failed")}`);
        }
      }
      if (ok) toast.success(`${ok} / ${files.length}`);
      await load();
    } finally {
      setBusy(false); if (inputRef.current) inputRef.current.value = "";
    }
  };
  const backend = BACKEND_URL || "";   // Batch 19 · Stage D: the one build setting, read in lib/api.js
  return (
    <div data-testid="hfws-pye-docs-block">
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" multiple onChange={onFiles} disabled={disabled || busy}
               accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.xlsx,.xls"
               className="text-[11px]"
               data-testid="hfws-pye-docs-input" />
        {busy && <span className="text-[10px] text-muted-foreground">…</span>}
      </div>
      {items.length > 0 && (
        <div className="mt-1 max-h-32 overflow-auto rounded border" data-testid="hfws-pye-docs-list">
          <table className="w-full text-[11px]">
            <thead className="border-b bg-slate-50 dark:bg-slate-900/40">
              <tr>
                <th className="px-2 py-1 text-start">{t("hfWs.cutoff.supportDocLabel")}</th>
                <th className="px-2 py-1 text-start">{t("hfWs.cutoff.uploadedBy")}</th>
                <th className="px-2 py-1 text-start">{t("hfWs.cutoff.uploadedAt")}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((it) => (
                <tr key={it.id} data-testid={`hfws-pye-doc-${it.id}`}>
                  <td className="px-2 py-1">
                    <a href={`${backend}/api/attachments/${it.id}/download`} target="_blank" rel="noreferrer"
                       className="text-blue-700 underline">{it.filename}</a>
                  </td>
                  <td className="px-2 py-1">{it.uploaded_by_name || "—"}</td>
                  <td className="px-2 py-1 font-mono">{it.uploaded_at ? fmtDateTimeFirm(it.uploaded_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
